import { useState, useEffect } from 'react';

/**
 * Hook to detect if the app is running as a PWA (installed)
 * Uses multiple detection methods for better compatibility
 */
export function usePWADetection() {
  const [isPWA, setIsPWA] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    // Multiple ways to detect PWA mode
    const detectPWA = () => {
      if (!isMounted) return;
      
      // Method 1: Check display mode (most reliable)
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      
      // Method 2: Check if opened from home screen (iOS Safari)
      const isIOSStandalone = (window.navigator as any).standalone === true;
      
      // Method 3: Check window.navigator.standalone (iOS specific)
      const hasNavigatorStandalone = 'standalone' in window.navigator;
      
      // Method 4: Check for TWA (Trusted Web App) on Android
      const isTWA = document.referrer.includes('android-app://');
      
      const detectedPWA = isStandalone || isIOSStandalone || isTWA;
      
      // PWA detection complete
      
      if (isMounted) {
        setIsPWA(detectedPWA);
      }
    };

    // Detect install prompt availability
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      if (isMounted) {
        setIsInstallable(true);
      }
    };

    // Run detection immediately
    detectPWA();
    
    // Listen for beforeinstallprompt event (indicates app is installable)
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Also listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addEventListener('change', detectPWA);

    return () => {
      isMounted = false;
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      mediaQuery.removeEventListener('change', detectPWA);
    };
  }, []);

  return {
    isPWA,
    isInstallable,
    // Helper function to determine if install button should be shown
    shouldShowInstallButton: !isPWA && isInstallable
  };
}